import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft } from "lucide-react";
import { M } from "../theme";

/**
 * A full-screen page over the tabs (the app's modal screens: a chat thread,
 * Configuration): white header with a back arrow, a 16px SemiBold title and
 * an optional subtitle, the body below. Slides up like the app's EdgeSwipeBack
 * entry. Escape closes it.
 */
export default function FullScreen({
  open,
  onClose,
  title,
  subtitle,
  right,
  children,
  header,
  bg = M.bg,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  /** Replaces the default title block (keeps the back arrow). */
  header?: ReactNode;
  children: ReactNode;
  bg?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div
      className="xm fixed inset-0 z-[200] flex flex-col animate-[xm-rise_260ms_cubic-bezier(.2,.8,.2,1)]"
      style={{ backgroundColor: bg }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex shrink-0 items-center gap-2.5 border-b bg-white px-3.5 pb-2.5"
        style={{ borderColor: "rgba(5,25,18,0.07)", paddingTop: "calc(env(safe-area-inset-top) + 8px)" }}
      >
        <button type="button" onClick={onClose} aria-label="Back" className="flex size-9 shrink-0 items-center justify-center rounded-full active:scale-[0.92]">
          <ArrowLeft size={22} color={M.text} strokeWidth={2} />
        </button>
        {header ?? (
          <div className="min-w-0 flex-1">
            {title ? (
              <p className="truncate text-[16px] font-semibold tracking-[-0.3px]" style={{ color: M.text }}>
                {title}
              </p>
            ) : null}
            {subtitle ? (
              <p className="truncate text-[10.5px]" style={{ color: M.textMuted }}>
                {subtitle}
              </p>
            ) : null}
          </div>
        )}
        {right}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>,
    document.body,
  );
}
